import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * Eligibility history — per-practice audit trail of payer coverage checks.
 * The server endpoint stores whatever the caller POSTs; it does not talk
 * to a payer clearinghouse. Today every entry is captured manually by a
 * user who called the payer by phone; once Office Ally is wired up, the
 * same endpoint will also receive auto-generated entries from a 270/271
 * transaction.
 *
 * `client` is stored as a free-text name on the JSONB blob, not a FK, so
 * per-client filtering happens client-side. The list volume is low enough
 * per practice that a single fetch + in-memory filter is fine; revisit
 * with a server-side filter if a practice starts accumulating thousands
 * of rows.
 */

export interface EligibilityCheck {
    id: string;
    checked: string;
    client: string;
    payer: string;
    memberId: string;
    status: string;
    deductible: string;
    copay: string;
    authOnFile: boolean;
    checkedBy: string;
}

export interface RecordEligibilityPayload {
    client: string;
    payer: string;
    memberId: string;
    status: string;
    deductible: string;
    copay: string;
    authOnFile: boolean;
    checkedBy: string;
}

async function fetchEligibilityHistory(): Promise<EligibilityCheck[]> {
    const res = await api.get<EligibilityCheck[]>('/eligibility-history');
    return res.data;
}

async function recordEligibilityCheck(
    payload: RecordEligibilityPayload,
): Promise<EligibilityCheck> {
    const res = await api.post<EligibilityCheck>('/eligibility-history', payload);
    return res.data;
}

/**
 * Returns the eligibility-history rows for a single client, matched by
 * the full-name string stored on each row. Rows are kept in the
 * server-returned order (newest `checked_at` first).
 */
export function useClientEligibilityHistory(clientFullName: string | undefined) {
    return useQuery({
        queryKey: ['eligibility-history'] as const,
        queryFn: fetchEligibilityHistory,
        staleTime: 30_000,
        select: (rows) =>
            clientFullName
                ? rows.filter((r) => r.client === clientFullName)
                : rows,
    });
}

export function useRecordEligibilityCheck() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: recordEligibilityCheck,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['eligibility-history'] });
        },
    });
}
